import React, { useEffect, useState } from "react";
import { fetchFeaturedComments } from "@/lib/api";

/**
 * Rotating one-quote testimonial. Renders a moderated (or admin-featured)
 * blog comment near the landing-page CTA. Featured comments come first,
 * the rest of the approved pool fills in so the rotator is never stale.
 *
 * Empty state: renders nothing (a landing without social proof is better
 * than an empty testimonial card).
 */
export default function LandingTestimonial() {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  const [hasFeatured, setHasFeatured] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchFeaturedComments(8);
        if (cancelled) return;
        setItems(res.comments || []);
        setHasFeatured(!!res.has_featured);
      } catch {
        /* silent: rotator is optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setI((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const current = items[i];

  return (
    <figure
      className="mt-10 sm:mt-14 max-w-2xl border-l-2 border-terracotta pl-5 sm:pl-6 py-2"
      aria-live="polite"
      data-testid="landing-testimonial"
    >
      <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-3">
        {hasFeatured ? "Reader reflection" : "From the journal comments"}
      </p>
      <blockquote
        key={current.id}
        className="font-serif italic text-bone-200 text-lg leading-relaxed animate-fade-up"
        data-testid={`landing-testimonial-quote-${i}`}
      >
        &ldquo;{current.body.length > 260 ? current.body.slice(0, 259).trim() + "…" : current.body}&rdquo;
      </blockquote>
      <figcaption className="mt-3 font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500">
        — {current.author_name}
        {current.post_slug ? ` · on /${current.post_slug}` : ""}
      </figcaption>
      {items.length > 1 && (
        <div
          className="mt-4 flex items-center gap-1"
          role="tablist"
          aria-label="Testimonial navigation"
        >
          {items.map((c, idx) => (
            <button
              key={c.id}
              onClick={() => setI(idx)}
              role="tab"
              aria-selected={idx === i}
              aria-label={`Show testimonial ${idx + 1} of ${items.length}`}
              data-testid={`landing-testimonial-dot-${idx}`}
              className="inline-flex items-center justify-center min-h-[32px] min-w-[32px] p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
            >
              <span
                className={`block h-1.5 rounded-none transition-all ${
                  idx === i
                    ? "w-6 bg-terracotta"
                    : "w-1.5 bg-bone-500/40 group-hover:bg-bone-400"
                }`}
                aria-hidden="true"
              />
            </button>
          ))}
        </div>
      )}
    </figure>
  );
}
